import { Icon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormControl,
  Heading,
  Image,
  Stack,
  useMediaQuery,
  useToast,
} from '@chakra-ui/react';
import { taskAction } from 'actions';
import Textarea from 'components/Textarea';
import { useFormik } from 'formik';
import { SET_TIMEOUT_STREAMING_ANSWER_Task } from 'helpers/constant';
import { TASK_FILE_STATUS, TASK_STATUS } from 'helpers/enum';
import { getUserInfoStorage } from 'helpers/lib';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiSidebarSimpleBold } from 'react-icons/pi';
import { RiSendPlaneFill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { SummaryService } from 'services/summary/summary.service';

import Message from './Message';
import introductionIcon from '../../assets/image/introduction.png';

interface ChatProps {
  onAddNew: () => void;
  onOpenSidebarMobile: () => void;
}

const Chat = ({ onAddNew, onOpenSidebarMobile }: ChatProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 30em)');
  const [isTablet] = useMediaQuery('(max-width: 48em)');

  const chatRef = useRef<HTMLDivElement>(null);
  const cancelEditRef = useRef<HTMLButtonElement>(null);
  const userInfo = getUserInfoStorage();

  const dispatch = useDispatch();
  const { status, current, isLimitReached, processingStreamingChunkStatusTask } = useSelector(
    (state) => state.task
  );

  const isExistFileProcessing = current.selectedFiles.some(
    (file) => file.task_file_status === TASK_FILE_STATUS.UPLOADED
  );
  const isGenerateAnswer = status === TASK_STATUS.GENERATE_ANSWER;
  const isDisableQuestionBox =
    !current?.task_id ||
    isGenerateAnswer ||
    isLimitReached ||
    Boolean(current.answerChunks.part_of_answer);
  let timer: ReturnType<typeof setTimeout> | any;

  const { values, handleChange, handleSubmit, resetForm } = useFormik({
    initialValues: {
      question: '',
    },
    onSubmit: (values) => {
      if (current.selectedFiles.length === 0) {
        toast({
          title: `Please add files before submit`,
          status: 'warning',
          position: 'bottom-right',
          isClosable: true,
        });
        return;
      }
      if (isExistFileProcessing) {
        toast({
          title: `Please wait file finish processing before submit`,
          status: 'warning',
          position: 'bottom-right',
          isClosable: true,
        });
        return;
      }
      if (values.question && values.question?.trim()) {
        if (current?.task_id) {
          dispatch(
            taskAction.addNewQuestion({
              ...values,
              created_at: new Date().toISOString(),
            })
          );
          dispatch(taskAction.updateStatus(TASK_STATUS.GENERATE_ANSWER));
          dispatch(taskAction.updateProcessTaskId(current.task_id));
          dispatch(taskAction.updateProcessingStreamingChunkStatusTask(true));
          dispatch(taskAction.setTimeOutForFinalAnswerTask(true));
        }
        resetForm();
      }
      cancelEditRef?.current?.click();
    },
  });

  useEffect(() => {
    if (current.setTimeOutFinalAnswerTask && current.task_id) {
      timer = setTimeout(async () => {
        const response = await SummaryService.getLatestMessageTask({
          id: current.task_id!,
        });

        if (response) {
          dispatch(
            taskAction.updateNewAnswer({
              id: response.id,
              answer: response.answer,
              timestamp: response.updated_at,
              question: response.question,
            })
          );
          dispatch(taskAction.clearStreamingAnswerChunksTask());
          console.log('chat submit');
        } else {
          dispatch(taskAction.clearStreamingAnswerChunksTask());
          dispatch(taskAction.updateProcessingStreamingChunkStatusTask(false));
        }
      }, SET_TIMEOUT_STREAMING_ANSWER_Task);
    }

    return () => clearTimeout(timer);
  }, [current.setTimeOutFinalAnswerTask, current.task_id]);

  const isDisableSubmit =
    !values.question || isGenerateAnswer || isLimitReached || processingStreamingChunkStatusTask;

  const onChangeMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleChange(e);
  };

  useEffect(() => {
    const scrollToBottom = () => {
      const chatElement = document.getElementById('message-container');
      if (chatElement) {
        chatElement.scrollTo({
          top: chatElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    };

    scrollToBottom();

    return () => {};
  }, [current, status]);

  return (
    <Flex
      direction={'column'}
      w={'100%'}
      // pos='relative'
      // w={{ base: '100vw', lg: '100%' }}
      // h={{ base: 'calc(100vh - 100px)', lg: '100%' }}
      // bg='bg_main_container'
      // borderRadius={{ lg: '20px' }}
      // shadow={{ lg: 'shadow_box' }}
      // style={{
      //   overflow: 'hidden',
      // }}
      // h={'100%'}
    >
      {!current?.task_id ? (
        <Flex
          align='center'
          justify={'center'}
          direction='column'
          flex={'1 1 0'}
          alignSelf={'center'}
          overflow={'auto'}
          w={{ lg: 'auto', base: '400px' }}
          minH={0}
          px={5}
        >
          <Image
            boxSize={{ lg: '64px', base: '50px' }}
            mb='16px'
            alt='introduction'
            src={introductionIcon}
          />
          <Box
            mb='26px'
            color='color_main_text'
            fontSize={{ base: 'xs', lg: 'large' }}
            textAlign='center'
          >
            <Heading as={'h2'} my={2} size={{ base: 'md', lg: 'lg' }}>
              {t('summary.welcome')}
            </Heading>
            <Box p={{ base: 5, md: 0 }} fontSize={{ base: 'small', md: 'medium' }}>
              {t('summary.description')}
            </Box>
          </Box>
          <Button
            flexShrink={0}
            w={{ lg: '300px', base: '200px' }}
            h={{ base: '40px', md: '64px' }}
            color='#ffffff'
            fontSize={{ base: '16px', md: '20px' }}
            borderRadius='30px'
            onClick={onAddNew}
            variant='submit_summary'
          >
            {t('summary.start')}
          </Button>
        </Flex>
      ) : (
        <Box
          ref={chatRef}
          sx={{
            '&::-webkit-scrollbar': {
              display: isMobile ? 'none' : 'block',
            },
          }}
          flex={'1 1 0'}
          alignSelf={'stretch'}
          overflow={'auto'}
          minH={0}
          id='message-container'
        >
          <Box maxW={{ lg: '1100px', base: '100%' }} m='auto'>
            <Stack direction='column' pt={{ base: 5, lg: 'auto' }} spacing={{ lg: '2rem' }}>
              {current?.data?.length > 0 &&
                current.data.map((message, index) => (
                  <Message
                    isLast={index === current.data.length - 1}
                    key={index}
                    data={message}
                    status={status}
                    user={userInfo!}
                    cancelEditRef={cancelEditRef}
                  />
                ))}
              {/* <div ref={chatRef} /> */}
            </Stack>
          </Box>
        </Box>
      )}
      <Box mt={5}>
        <form onSubmit={handleSubmit}>
          <FormControl>
            <Box pos='relative' gap={1} display={{ base: 'flex' }} w='100%' m='auto' px={8}>
              <Button
                display={{ base: 'block', xl: 'none' }}
                h='54px'
                minH={'52px'}
                maxH='54px'
                color='grey.50'
                onClick={onOpenSidebarMobile}
                variant='submit_summary'
              >
                <PiSidebarSimpleBold />
              </Button>
              <Textarea
                name='question'
                placeholder={t('common.inputPlaceholder')}
                minH={'52px'}
                maxH={isTablet ? '54px' : 'none'}
                borderRadius={'10px'}
                pe={12}
                _focus={{ border: 'border_main_summary' }}
                maxExpandHeight={200}
                value={values.question}
                fontSize={{ base: 'small', md: 'medium', lg: 'large' }}
                onChange={onChangeMessage}
                isDisabled={isDisableQuestionBox}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }
                }}
              />
              {!isDisableSubmit && (
                <Icon
                  as={RiSendPlaneFill}
                  boxSize={8}
                  position='absolute'
                  right={12}
                  bottom={4}
                  zIndex={999}
                  cursor='pointer'
                  _hover={{ opacity: '0.7' }}
                  onClick={() => handleSubmit()}
                />
              )}
            </Box>
          </FormControl>
        </form>
      </Box>
    </Flex>
  );
};

export default Chat;
